import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import "../features.css";
import API from "../../utils/API";
import { FEATURES } from "../../config/features";
import { useAuth } from "../../auth/AuthProvider";
import { tierCan } from "../../auth/permissions";
import { resolveAgentName, useBookData } from "../../data/BookDataProvider";
import { daysUntil, formatDate } from "../../data/format";
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  Modal,
  Segmented,
  Select,
  Field,
  useToast,
  PipelineIcon
} from "../../ui";
import {
  PIPELINE_STAGES,
  prospectStageInfo,
  caseClientRef,
  isRetentionCase,
  quotesForCase
} from "../shared";
import LinkClientAction from "./LinkClientAction";
import CaseQuotesDrawer from "./CaseQuotesDrawer";

/**
 * Pipeline (/app/pipeline) — R5 (referral: "My referrals") / W9 (staff:
 * all agents, filterable). List view default + board toggle; stages are
 * ROLE-DESIGN Part 3's enum, read from the additive `pipelineStage` field
 * (absent → new_referral).
 *
 * Stage MOVES are behind FEATURES.pipelineWrites until Session H's
 * stage-change endpoint ratifies: with the flag off there is no move
 * affordance at all (complete, not disabled). Lost always asks a reason.
 */

const LOST_REASONS = [
  { value: "price", label: "Price" },
  { value: "no_response", label: "No response" },
  { value: "declined", label: "Declined" },
  { value: "other", label: "Other" }
];

// C-2 (E's cutover read): the board renders one DOM node per card, so a stage
// holding the whole imported book (557 "new referral" cards) wedged scrolling.
// Cap each column and let the user opt into the rest. The List view stays the
// default and is paginated, so this only bounds the opt-in board.
const BOARD_COLUMN_CAP = 50;

function daysInStage(prospect) {
  const history = Array.isArray(prospect.stageHistory) ? prospect.stageHistory : [];
  const last = history.length > 0 ? history[history.length - 1] : null;
  const since = (last && last.at) || prospect.created_at;
  const days = since ? -daysUntil(since) : null;
  return days == null || days < 0 ? 0 : days;
}

/** Stage label, retention-aware (E QA LOW): a retention case at stage 0 reads
    "New case", not "New referral" — the record isn't a referral. */
function stageLabel(prospect) {
  const info = prospectStageInfo(prospect.pipelineStage);
  if (
    FEATURES.retention &&
    isRetentionCase(prospect) &&
    (prospect.pipelineStage || "new_referral") === "new_referral"
  ) {
    return "New case";
  }
  return info.label;
}

function StageMover({ prospect, onMoved }) {
  const toast = useToast();
  const [lostPrompt, setLostPrompt] = useState(false);
  const [lostReason, setLostReason] = useState("price");
  const [saving, setSaving] = useState(false);

  const move = async (stage, reason) => {
    setSaving(true);
    try {
      await API.setProspectStage(prospect._id, {
        stage,
        lostReason: stage === "lost" ? reason : undefined
      });
      toast.success(`Moved to ${prospectStageInfo(stage).label}.`);
      onMoved();
    } catch (err) {
      toast.error("That move didn't save — try again.");
    } finally {
      setSaving(false);
      setLostPrompt(false);
    }
  };

  const current = prospect.pipelineStage || "new_referral";

  return (
    <>
      <Select
        aria-label={`Stage for ${prospect.name}`}
        className="feat-filter"
        value={current}
        disabled={saving}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          const next = e.target.value;
          if (next === current) {
            return;
          }
          if (next === "lost") {
            setLostPrompt(true);
          } else {
            move(next);
          }
        }}
      >
        {PIPELINE_STAGES.map((s) => (
          <option key={s.key} value={s.key}>
            {s.label}
          </option>
        ))}
      </Select>

      <Modal
        open={lostPrompt}
        onClose={() => setLostPrompt(false)}
        title={`Mark ${prospect.name} as Lost`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setLostPrompt(false)} disabled={saving}>
              Cancel
            </Button>
            <Button variant="destructive" loading={saving} onClick={() => move("lost", lostReason)}>
              Mark lost
            </Button>
          </>
        }
      >
        <Field label="Why was it lost?" htmlFor={`lost-${prospect._id}`}>
          <Select
            id={`lost-${prospect._id}`}
            value={lostReason}
            onChange={(e) => setLostReason(e.target.value)}
          >
            {LOST_REASONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </Select>
        </Field>
      </Modal>
    </>
  );
}

function PipelinePage() {
  const { tier, agents } = useAuth();
  const can = tierCan(tier);
  const { loading, prospects, quotes, refresh } = useBookData();
  const [view, setView] = useState("list");
  const [agentFilter, setAgentFilter] = useState("");
  const [caseFilter, setCaseFilter] = useState("all"); // all | prospect | retention
  const [openCaseId, setOpenCaseId] = useState(null); // D6 quotes drawer
  const [expandedCols, setExpandedCols] = useState({}); // board: stage.key → show all

  // Deep-link target (?prospect=<id>) — the digest/monthly emails' pinned
  // prospect URL (confirmed with the Coordinator): open in list view,
  // scroll to and pulse the row (works in board view too via the same class).
  const [searchParams] = useSearchParams();
  const targetId = searchParams.get("prospect");
  const openQuotes = searchParams.get("quotes"); // ?prospect=<id>&quotes=1 (R7 deep-link)
  useEffect(() => {
    if (!targetId || loading) {
      return undefined;
    }
    // D6: an email link with &quotes=1 opens the case quotes drawer directly.
    if (FEATURES.proposalDocs && openQuotes) {
      setOpenCaseId(targetId);
    }
    const t = window.setTimeout(() => {
      const el = document.querySelector(".feat-target-pulse");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
    return () => window.clearTimeout(t);
  }, [targetId, loading, openQuotes]);

  // If the deep-link target sits in a capped board column, pre-expand that
  // column so its pulse row actually renders (list view isn't capped).
  useEffect(() => {
    if (!targetId || loading) {
      return;
    }
    const target = (prospects || []).find((p) => p._id === targetId);
    if (target) {
      const stage = target.pipelineStage || "new_referral";
      setExpandedCols((prev) => (prev[stage] ? prev : { ...prev, [stage]: true }));
    }
  }, [targetId, loading, prospects]);

  // D6 (Coordinator ruling B): the "N quotes" chip shows only when a case has
  // ≥1 quote (client-keyed) — no quotes → no chip (E's no-dead-affordance law).
  const caseQuotesFor = (p) => (FEATURES.proposalDocs ? quotesForCase(p, quotes) : []);
  const openCase = openCaseId ? prospects.find((p) => p._id === openCaseId) || null : null;
  const renderQuotesChip = (p) => {
    const count = caseQuotesFor(p).length;
    if (count === 0) {
      return null;
    }
    return (
      <button
        type="button"
        className="feat-caseq-chip"
        onClick={(e) => {
          e.stopPropagation();
          setOpenCaseId(p._id);
        }}
      >
        {count} quote{count === 1 ? "" : "s"}
      </button>
    );
  };

  const rows = useMemo(() => {
    let list = prospects;
    if (agentFilter) {
      list = list.filter((p) => String(p.referrerRef || p.referrerID) === agentFilter);
    }
    if (FEATURES.retention && caseFilter !== "all") {
      list = list.filter((p) =>
        caseFilter === "retention" ? isRetentionCase(p) : !isRetentionCase(p)
      );
    }
    return list;
  }, [prospects, agentFilter, caseFilter]);

  const open = rows.filter((p) => !["bound", "lost"].includes(p.pipelineStage || "new_referral"));
  const past = rows.filter((p) => ["bound", "lost"].includes(p.pipelineStage || "new_referral"));

  // E QA LOW: pluralize, and read "retention case(s)" under the retention
  // filter rather than "open referrals".
  const openCountLabel =
    FEATURES.retention && caseFilter === "retention"
      ? open.length === 1
        ? "retention case"
        : "retention cases"
      : open.length === 1
        ? "open referral"
        : "open referrals";

  const columns = useMemo(() => {
    const cols = [
      {
        key: "name",
        header: "Prospect",
        sortValue: (p) => (p.name || "").toLowerCase(),
        render: (p) => {
          const retention = FEATURES.retention && isRetentionCase(p);
          const clientId = retention ? caseClientRef(p) : null;
          return (
            <span className="feat-cell-stack">
              <span className="feat-pipeline-name">
                <span className="feat-cell-primary">{p.name}</span>
                {retention ? <Badge variant="info">Retention</Badge> : null}
                {renderQuotesChip(p)}
              </span>
              <span className="feat-cell-secondary">
                {[p.lines, p.email || p.telephone].filter(Boolean).join(" · ")}
                {clientId ? (
                  <>
                    {" · "}
                    <Link
                      to={`/app/clients/${clientId}`}
                      onClick={(e) => e.stopPropagation()}
                      className="feat-pipeline-caselink"
                    >
                      View client ↗
                    </Link>
                  </>
                ) : null}
              </span>
              {/* M-3 (E QA): the latest note — the one field that says what's
                  actually happening without opening the record. */}
              {p.notes ? <span className="feat-pipeline-note">“{p.notes}”</span> : null}
            </span>
          );
        }
      },
      {
        key: "stage",
        header: "Stage",
        sortValue: (p) =>
          PIPELINE_STAGES.findIndex((s) => s.key === (p.pipelineStage || "new_referral")),
        render: (p) => {
          const info = prospectStageInfo(p.pipelineStage);
          return <Badge variant={info.variant}>{stageLabel(p)}</Badge>;
        }
      },
      {
        key: "age",
        header: "In stage",
        align: "num",
        sortValue: (p) => daysInStage(p),
        render: (p) => `${daysInStage(p)}d`
      },
      {
        key: "renewal",
        header: "Key date",
        sortValue: (p) => (p.renewalDate ? new Date(p.renewalDate).getTime() : null),
        render: (p) => (p.renewalDate ? formatDate(p.renewalDate) : "—")
      }
    ];
    if (can.isStaff) {
      cols.push({
        key: "referrer",
        header: "Referred by",
        render: (p) =>
          resolveAgentName(agents, p.referrerRef || p.referrerID) || (
            <span className="fk-muted">—</span>
          )
      });
    }
    if (FEATURES.pipelineWrites) {
      cols.push({
        key: "move",
        header: "Move",
        sortable: false,
        render: (p) => <StageMover prospect={p} onMoved={refresh} />
      });
    }
    if (can.isStaff && FEATURES.linkClient) {
      cols.push({
        key: "link",
        header: "",
        sortable: false,
        render: (p) => <LinkClientAction prospect={p} onLinked={refresh} />
      });
    }
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [can.isStaff, agents, refresh, quotes]);

  const boardStages = PIPELINE_STAGES.filter((s) => !s.terminal);

  return (
    <div className="shell-page">
      <div className="shell-page__titlerow">
        <h1 className="shell-page__title">{tier === "referral" ? "My referrals" : "Pipeline"}</h1>
        {/* E QA HIGH: the tool cluster is one flex-wrapping group so it drops
            to its own row under the title rather than side-scrolling at 375px. */}
        <div className="feat-titlerow-tools">
          <Segmented
            aria-label="Pipeline view"
            options={[
              { value: "list", label: "List" },
              { value: "board", label: "Board" }
            ]}
            value={view}
            onChange={setView}
          />
          {can.isStaff ? (
            <Select
              className="feat-filter"
              aria-label="Filter by referring agent"
              value={agentFilter}
              onChange={(e) => setAgentFilter(e.target.value)}
            >
              <option value="">All agents</option>
              {(agents || []).map((a) => (
                <option key={a._id} value={String(a._id)}>
                  {[a.firstName, a.lastName].filter(Boolean).join(" ") || a.email}
                </option>
              ))}
            </Select>
          ) : null}
          {FEATURES.retention ? (
            <Segmented
              aria-label="Case type"
              options={[
                { value: "all", label: "All" },
                { value: "prospect", label: "Referrals" },
                { value: "retention", label: "Retention" }
              ]}
              value={caseFilter}
              onChange={setCaseFilter}
            />
          ) : null}
        </div>
      </div>

      {view === "list" ? (
        <DataTable
          columns={columns}
          rows={open}
          rowKey={(p) => p._id}
          rowClassName={(p) => (targetId && p._id === targetId ? "feat-target-pulse" : "")}
          loading={loading}
          countLabel={openCountLabel}
          searchPlaceholder="Search name, email, phone…"
          searchText={(p) => [p.name, p.email, p.telephone, p.lines].filter(Boolean).join(" ")}
          emptyState={
            <EmptyState
              icon={<PipelineIcon size={32} />}
              title={
                FEATURES.retention && caseFilter === "retention"
                  ? "No open retention cases"
                  : "No open referrals"
              }
              message={
                FEATURES.retention && caseFilter === "retention"
                  ? "Client re-quotes in progress land here."
                  : "New referrals land here in the New referral stage."
              }
            />
          }
        />
      ) : (
        <div className="feat-board">
          {boardStages.map((stage) => {
            const stageCards = open.filter(
              (p) => (p.pipelineStage || "new_referral") === stage.key
            );
            const shown = expandedCols[stage.key]
              ? stageCards
              : stageCards.slice(0, BOARD_COLUMN_CAP);
            return (
              <div key={stage.key} className="feat-board__col">
                <div className="feat-board__head">
                  <span>{stage.label}</span>
                  <span>{stageCards.length}</span>
                </div>
                {shown.map((p) => (
                  <div
                    key={p._id}
                    className={`feat-board__card${
                      targetId && p._id === targetId ? " feat-target-pulse" : ""
                    }`}
                  >
                    <span className="feat-board__card-title feat-pipeline-name">
                      {p.name}
                      {FEATURES.retention && isRetentionCase(p) ? (
                        <Badge variant="info">Retention</Badge>
                      ) : null}
                      {renderQuotesChip(p)}
                    </span>
                    <span className="feat-board__card-meta">
                      {[p.lines, `${daysInStage(p)}d in stage`].filter(Boolean).join(" · ")}
                    </span>
                    {p.notes ? (
                      <span className="feat-board__card-meta feat-pipeline-note">“{p.notes}”</span>
                    ) : null}
                    {can.isStaff && (p.referrerRef || p.referrerID) ? (
                      <span className="feat-board__card-meta">
                        via {resolveAgentName(agents, p.referrerRef || p.referrerID) || "—"}
                      </span>
                    ) : null}
                    {FEATURES.retention && isRetentionCase(p) && caseClientRef(p) ? (
                      <Link
                        to={`/app/clients/${caseClientRef(p)}`}
                        className="feat-board__card-meta feat-pipeline-caselink"
                      >
                        View client ↗
                      </Link>
                    ) : null}
                    {FEATURES.pipelineWrites ? <StageMover prospect={p} onMoved={refresh} /> : null}
                    {can.isStaff && FEATURES.linkClient ? (
                      <LinkClientAction prospect={p} onLinked={refresh} />
                    ) : null}
                  </div>
                ))}
                {stageCards.length > shown.length ? (
                  <button
                    type="button"
                    className="feat-board__more"
                    onClick={() => setExpandedCols((prev) => ({ ...prev, [stage.key]: true }))}
                  >
                    Show all {stageCards.length}
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {past.length > 0 ? (
        <div style={{ marginTop: "var(--space-6)" }}>
          <h2 className="feat-nudges__title">Past prospects</h2>
          <p className="feat-nudges__sub">
            Bound and lost referrals stay tracked — renewals and anniversaries can bring them back.
          </p>
          <DataTable
            columns={[
              {
                key: "name",
                header: "Prospect",
                sortValue: (p) => (p.name || "").toLowerCase(),
                render: (p) => <span className="feat-cell-primary">{p.name}</span>
              },
              {
                key: "outcome",
                header: "Outcome",
                sortable: false,
                render: (p) => {
                  const info = prospectStageInfo(p.pipelineStage);
                  return (
                    <span className="feat-badges">
                      <Badge variant={info.variant}>{info.label}</Badge>
                      {p.lostReason ? (
                        <span className="feat-cell-secondary">
                          {LOST_REASONS.find((r) => r.value === p.lostReason)?.label ||
                            p.lostReason}
                        </span>
                      ) : null}
                    </span>
                  );
                }
              },
              {
                key: "key",
                header: "Key date",
                sortValue: (p) => (p.renewalDate ? new Date(p.renewalDate).getTime() : null),
                render: (p) =>
                  p.renewalDate ? `renews ${formatDate(p.renewalDate)}` : "anniversary"
              }
            ]}
            rows={past}
            rowKey={(p) => p._id}
            countLabel="past prospects"
          />
        </div>
      ) : null}

      <CaseQuotesDrawer
        prospect={openCase}
        quotes={caseQuotesFor(openCase)}
        onClose={() => setOpenCaseId(null)}
      />
    </div>
  );
}

export default PipelinePage;
